import React from 'react';

// ─── Design tokens ───────────────────────────────────────────────────────────
const C = {
  bg: '#08090f', card: '#0d1117', inp: '#0f1420',
  border: '#1f2937', borderS: '#1a2035',
  primary: '#e5e7eb', secondary: '#6b7280', muted: '#374151',
  profit: '#16C784', loss: '#ef4444', accent: '#6366f1',
};

// ─── Domain helpers ───────────────────────────────────────────────────────────
function scoreColor(s) {
  if (s >= 81) return '#16C784';
  if (s >= 61) return '#6366F1';
  if (s >= 41) return '#F59E0B';
  if (s >= 21) return '#f97316';
  return '#ef4444';
}

const COMP_LABELS = {
  stop_loss: 'Stop Loss', risk_reward: 'Risk-Reward', strategy: 'Strategy',
  emotion: 'Emotion', frequency: 'Frequency', confidence: 'Confidence',
  journal: 'Journal', sl_violation: 'SL Violations', risk_profile: 'Risk Profile',
};

const TF_LABELS = { '1M': '1 Month', '3M': '3 Months', '6M': '6 Months', '1Y': '1 Year' };

// ─── Micro-components ─────────────────────────────────────────────────────────
function SDiv({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0' }}>
      <div style={{ flex: 1, height: 1, background: C.borderS }} />
      <span style={{ color: C.muted, fontSize: 9, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase' }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: C.borderS }} />
    </div>
  );
}

function SCard({ children, accent, style = {} }) {
  return (
    <div style={{
      borderRadius: 12, padding: '10px 12px', boxSizing: 'border-box',
      background: accent ? `linear-gradient(135deg,${accent}12 0%,${C.card} 100%)` : C.card,
      border: `1px solid ${accent ? `${accent}28` : C.border}`, ...style,
    }}>
      {children}
    </div>
  );
}

function SHead({ emoji, title, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
      <div style={{
        width: 20, height: 20, borderRadius: 6, background: `${color || C.accent}22`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 11,
      }}>
        {emoji}
      </div>
      <span style={{ color: C.primary, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1 }}>{title}</span>
    </div>
  );
}

function MC({ label, value, color }) {
  return (
    <div style={{
      padding: '5px 8px', borderRadius: 8,
      background: color ? `${color}10` : C.inp,
      border: `1px solid ${color ? `${color}22` : C.border}`,
    }}>
      <div style={{ color: C.secondary, fontSize: 8, fontWeight: 600, marginBottom: 1 }}>{label}</div>
      <div style={{ color: color || C.primary, fontSize: 11, fontWeight: 800, lineHeight: 1.2 }}>{value ?? '—'}</div>
    </div>
  );
}

function Pill({ label, color }) {
  return (
    <span style={{
      display: 'inline-block', background: `${color}18`, color,
      border: `1px solid ${color}28`, borderRadius: 20, padding: '2px 8px',
      fontSize: 9, fontWeight: 700, marginRight: 3, marginBottom: 3,
    }}>
      {label}
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
function PerformanceShareCard({
  summary, analytics, timeframe, disciplineScore,
  disciplineComponents, strategyBreakdown, performers, cardRef,
}) {
  const s   = summary   || {};
  const a   = analytics || {};
  const top = performers?.top    || [];
  const bot = performers?.bottom || [];
  const tags = (strategyBreakdown || []).slice(0, 3);

  const totalPnl   = s.total_pnl    ?? 0;
  const winRate    = s.win_rate      ?? 0;
  const wins       = s.wins          ?? 0;
  const losses     = s.losses        ?? 0;
  const trades     = s.closed_trades ?? (wins + losses);
  const avgWin     = s.avg_win       ?? 0;
  const avgLoss    = s.avg_loss      ?? 0;
  const avgRR      = a.avg_rr        ?? 0;
  const maxDD      = a.max_drawdown  ?? 0;
  const winStreak  = a.max_win_streak  ?? 0;
  const lossStreak = a.max_loss_streak ?? 0;
  const disc       = disciplineScore  ?? 0;

  const pf         = avgLoss !== 0 ? Math.abs(avgWin / avgLoss) : 0;
  const isPositive = totalPnl >= 0;
  const pnlColor   = isPositive ? '#16C784' : '#ef4444';
  const wrColor    = winRate >= 55 ? '#16C784' : winRate >= 40 ? '#f59e0b' : '#ef4444';
  const rrColor    = avgRR >= 2 ? '#16C784' : avgRR >= 1 ? '#f59e0b' : '#ef4444';
  const discColor  = scoreColor(disc);

  const today    = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  const tfLabel  = TF_LABELS[timeframe] || timeframe || 'Overall';
  const inr      = v => '₹' + Math.abs(Math.round(Number(v))).toLocaleString('en-IN');
  const fmtPnl   = v => (Number(v) >= 0 ? '+' : '−') + inr(v);
  const sym      = s => (s || '').replace('.NS', '');

  const circumference = 2 * Math.PI * 21;
  const discDash      = (disc / 100) * circumference;

  return (
    <div
      ref={cardRef}
      style={{
        width: 1080, height: 1350, background: C.bg, overflow: 'hidden', position: 'relative',
        fontFamily: "'Segoe UI',system-ui,-apple-system,sans-serif",
        display: 'flex', flexDirection: 'column', padding: 28, gap: 8, boxSizing: 'border-box',
      }}
    >
      {/* Ambient glow */}
      <div style={{
        position: 'absolute', top: -80, right: -80, width: 220, height: 220,
        borderRadius: '50%', background: `radial-gradient(circle,${pnlColor}0e 0%,transparent 65%)`,
        pointerEvents: 'none',
      }} />

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 8,
            background: 'linear-gradient(135deg,#22c55e30,#22c55e10)',
            border: '1px solid #22c55e35',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ color: '#22c55e', fontSize: 14, fontWeight: 900 }}>T</span>
          </div>
          <span style={{ color: '#fff', fontSize: 16, fontWeight: 800 }}>TradeReflex</span>
        </div>
        <span style={{ color: C.muted, fontSize: 11 }}>{today}</span>
        <span style={{ color: C.muted, fontSize: 11 }}>tradereflex.in</span>
      </div>

      {/* ── Hero ── */}
      <SCard accent={pnlColor}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
          {/* Left: P&L + badges */}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 8 }}>
              <Pill label={tfLabel} color="#818cf8" />
              <Pill label={isPositive ? 'PROFIT' : 'LOSS'} color={pnlColor} />
              <Pill label="Performance" color={C.secondary} />
            </div>
            <div style={{ color: pnlColor, fontSize: 42, fontWeight: 900, letterSpacing: '-2px', lineHeight: 1 }}>
              {fmtPnl(totalPnl)}
            </div>
            <div style={{ color: pnlColor, fontSize: 11, fontWeight: 700, marginTop: 4, opacity: 0.8 }}>
              across {trades} trades · {tfLabel}
            </div>
          </div>
          {/* Right: Win/Loss visual bar */}
          <div style={{ width: 180, flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ color: '#16C784', fontSize: 10, fontWeight: 700 }}>↑ {wins}W</span>
              <span style={{ color: '#ef4444', fontSize: 10, fontWeight: 700 }}>{losses}L ↓</span>
            </div>
            <div style={{ height: 8, borderRadius: 4, background: C.border, overflow: 'hidden', marginBottom: 4 }}>
              <div style={{
                width: `${trades > 0 ? (wins / trades) * 100 : 0}%`,
                height: '100%', background: 'linear-gradient(90deg,#16C784,#22d3ee)', borderRadius: 4,
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#16C784', fontSize: 9, fontWeight: 700 }}>{winRate.toFixed(1)}% win</span>
              <span style={{ color: '#ef4444', fontSize: 9, fontWeight: 700 }}>{(100 - winRate).toFixed(1)}% loss</span>
            </div>
          </div>
        </div>
      </SCard>

      {/* ── Key metrics row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {/* Left: Win Rate, Avg Win, Avg Loss, Avg R:R */}
        <SCard>
          <SHead emoji="📊" title="Performance" color="#6366f1" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <MC label="Win Rate"  value={`${winRate.toFixed(1)}%`}                 color={wrColor} />
            <MC label="Avg Win"   value={fmtPnl(avgWin)}                           color="#16C784" />
            <MC label="Avg Loss"  value={fmtPnl(avgLoss)}                          color="#ef4444" />
            <MC label="Avg R:R"   value={avgRR ? `${Number(avgRR).toFixed(2)}x` : '—'} color={rrColor} />
          </div>
        </SCard>
        {/* Right: Max DD, Best Streak, Worst Streak, Profit Factor */}
        <SCard>
          <SHead emoji="⚡" title="Risk & Streaks" color="#f59e0b" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <MC label="Max Drawdown"   value={maxDD > 0 ? '−' + inr(maxDD) : '—'}           color="#ef4444" />
            <MC label="Best Streak"    value={winStreak ? `${winStreak}W` : '—'}              color="#16C784" />
            <MC label="Worst Streak"   value={lossStreak ? `${lossStreak}L` : '—'}            color="#ef4444" />
            <MC label="Profit Factor"  value={pf > 0 ? `${pf.toFixed(2)}x` : '—'}
              color={pf >= 1.5 ? '#16C784' : pf >= 1 ? '#f59e0b' : '#ef4444'} />
          </div>
        </SCard>
      </div>

      {/* ── Trade Highlights ── */}
      <SDiv label="Trade Highlights" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {/* Left: Best + Worst trade */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {s.best_trade && (
            <SCard accent="#16C784">
              <div style={{ color: '#16C784', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>Best Trade</div>
              <div style={{ color: C.primary, fontSize: 13, fontWeight: 800, marginBottom: 2 }}>{sym(s.best_trade.symbol)}</div>
              <div style={{ color: '#16C784', fontSize: 16, fontWeight: 900 }}>+{inr(s.best_trade.pnl)}</div>
            </SCard>
          )}
          {s.worst_trade && (
            <SCard accent="#ef4444">
              <div style={{ color: '#ef4444', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>Worst Trade</div>
              <div style={{ color: C.primary, fontSize: 13, fontWeight: 800, marginBottom: 2 }}>{sym(s.worst_trade.symbol)}</div>
              <div style={{ color: '#ef4444', fontSize: 16, fontWeight: 900 }}>−{inr(Math.abs(s.worst_trade.pnl))}</div>
            </SCard>
          )}
        </div>
        {/* Right: Top 3 strategies */}
        <SCard style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <SHead emoji="🎯" title="By Strategy" color="#818cf8" />
          {tags.length > 0 ? tags.map(t => {
            const wr  = t.trades > 0 ? Math.round((t.wins / t.trades) * 100) : 0;
            const col = t.pnl >= 0 ? '#16C784' : '#ef4444';
            return (
              <div key={t.tag} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '5px 8px', borderRadius: 8, background: C.inp, border: `1px solid ${C.border}`,
              }}>
                <div>
                  <div style={{ color: '#818cf8', fontSize: 10, fontWeight: 800, textTransform: 'capitalize', marginBottom: 1 }}>{t.tag}</div>
                  <div style={{ color: C.secondary, fontSize: 8 }}>{t.trades}t · {wr}% win</div>
                </div>
                <div style={{ color: col, fontSize: 12, fontWeight: 900 }}>{t.pnl >= 0 ? '+' : '−'}{inr(Math.abs(t.pnl))}</div>
              </div>
            );
          }) : (
            <div style={{ color: C.secondary, fontSize: 10 }}>No strategy data</div>
          )}
        </SCard>
      </div>

      {/* ── Top & Worst Stocks ── */}
      <SDiv label="Top & Worst Stocks" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <SCard accent="#16C784">
          <SHead emoji="🚀" title="Top Performers" color="#16C784" />
          {top.slice(0, 3).map(p => (
            <div key={p.symbol} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ color: C.primary, fontSize: 11, fontWeight: 700 }}>{sym(p.symbol)}</span>
              <span style={{ color: '#16C784', fontSize: 11, fontWeight: 800 }}>+{inr(p.pnl)}</span>
            </div>
          ))}
          {top.length === 0 && <div style={{ color: C.secondary, fontSize: 10 }}>—</div>}
        </SCard>
        <SCard accent="#ef4444">
          <SHead emoji="📉" title="Worst Stocks" color="#ef4444" />
          {bot.slice(0, 3).map(p => (
            <div key={p.symbol} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ color: C.primary, fontSize: 11, fontWeight: 700 }}>{sym(p.symbol)}</span>
              <span style={{ color: '#ef4444', fontSize: 11, fontWeight: 800 }}>−{inr(Math.abs(p.pnl))}</span>
            </div>
          ))}
          {bot.length === 0 && <div style={{ color: C.secondary, fontSize: 10 }}>—</div>}
        </SCard>
      </div>

      {/* ── Discipline ── */}
      <SDiv label="Discipline" />
      <SCard accent={discColor}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Donut */}
          <div style={{ position: 'relative', width: 64, height: 64, flexShrink: 0 }}>
            <svg viewBox="0 0 52 52" width="64" height="64" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="26" cy="26" r="21" fill="none" strokeWidth="5" stroke={C.border} />
              <circle cx="26" cy="26" r="21" fill="none" strokeWidth="5" stroke={discColor}
                strokeDasharray={`${discDash} ${circumference}`} strokeLinecap="round" />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: discColor, fontSize: 14, fontWeight: 900, lineHeight: 1 }}>{disc}</span>
              <span style={{ color: C.secondary, fontSize: 8 }}>/100</span>
            </div>
          </div>

          {/* Component mini-bars 3-col */}
          <div style={{ flex: 1 }}>
            {disciplineComponents && Object.keys(disciplineComponents).length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '5px 12px' }}>
                {Object.entries(disciplineComponents).map(([k, v]) => {
                  const col = v >= 70 ? '#16C784' : v >= 50 ? '#f59e0b' : '#ef4444';
                  return (
                    <div key={k}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span style={{ color: C.secondary, fontSize: 8 }}>{COMP_LABELS[k] || k}</span>
                        <span style={{ color: col, fontSize: 8, fontWeight: 700 }}>{v}</span>
                      </div>
                      <div style={{ height: 3, borderRadius: 2, background: C.border, overflow: 'hidden' }}>
                        <div style={{ width: `${v}%`, height: '100%', background: col, borderRadius: 2 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ color: C.secondary, fontSize: 10 }}>No discipline data</div>
            )}
          </div>
        </div>
      </SCard>

      {/* ── Footer ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        paddingTop: 8, borderTop: `1px solid ${C.borderS}`, flexShrink: 0, marginTop: 'auto',
      }}>
        <span style={{ color: C.muted, fontSize: 10 }}>Know your edge · TradeReflex</span>
        <span style={{ color: C.secondary, fontSize: 10, fontWeight: 700 }}>tradereflex.in</span>
      </div>
    </div>
  );
}

export default PerformanceShareCard;
